import type { CanvasCapture } from '@repo/contracts';

import {
  AlertIcon,
  CameraIcon,
  CanvasIcon,
  CloseIcon,
  DownloadIcon,
} from '../lib/icons';
import { hostOf } from './target-url';

/**
 * The right-hand rail: the capture action, the way onto the canvas, and
 * everything captured so far.
 *
 * Built around scroll → capture → scroll → capture, so newest sits on top, the
 * count is always visible, and nothing here ever steals focus from the page.
 * `captures` is server state (useCaptures) in the API's order — oldest first —
 * and this component reverses it for display rather than sorting the source,
 * so a capture's number always matches the board's left-to-right order.
 */

function CaptureCard({
  capture,
  index,
  onRemove,
}: {
  capture: CanvasCapture;
  index: number;
  onRemove: (id: string) => void;
}) {
  const number = index + 1;

  return (
    <li className="group relative duration-300 animate-in fade-in slide-in-from-right-2 motion-reduce:animate-none">
      <figure className="overflow-hidden rounded-card bg-surface ring-1 ring-hairline transition group-hover:ring-hairline-alt">
        <img
          alt={`Capture ${String(number)} of ${hostOf(capture.pageUrl)}`}
          className="block aspect-16/10 w-full bg-warm object-cover object-top"
          src={capture.imageUrl}
        />
        <figcaption className="flex items-center gap-2 px-2.5 py-2">
          <span className="flex size-5 shrink-0 items-center justify-center rounded-pill bg-ink text-badge font-semibold text-highlight">
            {number}
          </span>
          <span className="min-w-0 flex-1 truncate text-xs text-text-secondary">
            {capture.name ?? hostOf(capture.pageUrl)}
          </span>
          <span className="shrink-0 text-tag tabular-nums text-text-muted">
            {capture.width}×{capture.height}
          </span>
        </figcaption>
      </figure>

      {/* Actions appear on hover/focus so the rail reads as a filmstrip rather
          than a toolbar repeated once per item. */}
      <div className="absolute top-2 right-2 flex gap-1 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
        <a
          aria-label={`Download capture ${String(number)}`}
          className="flex size-6 items-center justify-center rounded-pill bg-surface/95 text-text-secondary ring-1 ring-hairline backdrop-blur-sm transition hover:text-text-primary"
          download={`capture-${String(number)}.png`}
          href={capture.imageUrl}
          title="Download"
        >
          <DownloadIcon className="size-3.5" />
        </a>
        <button
          aria-label={`Delete capture ${String(number)}`}
          className="flex size-6 cursor-pointer items-center justify-center rounded-pill bg-surface/95 text-text-secondary ring-1 ring-hairline backdrop-blur-sm transition hover:text-critical"
          onClick={() => {
            onRemove(capture.id);
          }}
          title="Delete"
          type="button"
        >
          <CloseIcon className="size-3.5" />
        </button>
      </div>
    </li>
  );
}

export function CaptureRail({
  actionSlot,
  captureError,
  captures,
  capturing,
  disabled,
  emptyHint = 'Each shot lands here and on the canvas.',
  emptyTitle = 'Scroll, then capture',
  footerNote = 'Captured at 2× so text stays legible',
  onCapture,
  onOpenCanvas,
  onRemove,
}: {
  /**
   * Replaces the capture button.
   *
   * The same rail serves the worker (which captures on command) and the Chrome
   * extension (where the shutter is in another window entirely), and a rail
   * with a dead button in it would be a lie about who is in control. Everything
   * else — the count, the filmstrip, delete, Open canvas — is identical, which
   * is the point of sharing it.
   */
  actionSlot?: React.ReactNode;
  captureError?: null | string;
  captures: CanvasCapture[];
  capturing?: boolean;
  disabled?: boolean;
  emptyHint?: string;
  emptyTitle?: string;
  footerNote?: string;
  onCapture?: () => void;
  onOpenCanvas: () => void;
  onRemove: (id: string) => void;
}) {
  const empty = captures.length === 0;

  return (
    <aside className="flex w-[268px] shrink-0 flex-col border-l border-hairline bg-surface">
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <h2 className="!m-0 !text-ui !font-semibold !tracking-[0.06em] !text-text-muted uppercase">
          Captures
        </h2>
        <span className="rounded-pill bg-warm px-2 py-0.5 text-xs font-medium tabular-nums text-text-secondary">
          {captures.length}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">
        {empty ? (
          <div className="rounded-card border border-dashed border-hairline-alt px-4 py-7 text-center">
            <span className="mx-auto flex size-9 items-center justify-center rounded-pill bg-warm text-text-muted">
              <CameraIcon className="size-[18px]" />
            </span>
            <p className="mt-3 text-ui leading-snug font-medium text-text-primary">
              {emptyTitle}
            </p>
            <p className="mt-1 text-xs leading-snug text-text-muted">
              {emptyHint}
            </p>
          </div>
        ) : (
          <ul className="space-y-2.5">
            {[...captures].reverse().map((capture, index) => (
              <CaptureCard
                capture={capture}
                // Newest on top, numbered by capture order.
                index={captures.length - 1 - index}
                key={capture.id}
                onRemove={onRemove}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-hairline p-3">
        {captureError ? (
          <p className="mb-2.5 flex items-start gap-2 rounded-card bg-tag-critical px-3 py-2 text-xs leading-snug text-text-primary">
            <AlertIcon className="mt-px size-3.5 shrink-0 text-critical" />
            {captureError}
          </p>
        ) : null}

        {actionSlot ?? (
          <button
            className="flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-button bg-ink text-sm font-semibold text-white transition hover:bg-ink/90 active:scale-[0.98] disabled:cursor-default disabled:opacity-40 disabled:hover:bg-ink"
            disabled={disabled ?? capturing}
            onClick={onCapture}
            type="button"
          >
            {capturing ? (
              <>
                <span className="size-4 animate-spin rounded-pill border-2 border-white/40 border-t-white motion-reduce:animate-none" />
                Capturing…
              </>
            ) : (
              <>
                <CameraIcon className="size-[18px] text-highlight" />
                Capture viewport
                {/* The shortcut is the whole point of the loop, so it is on the
                    button rather than hidden in a tooltip. */}
                <kbd className="ml-0.5 rounded-badge bg-white/15 px-1.5 py-0.5 text-badge font-semibold text-white/70">
                  C
                </kbd>
              </>
            )}
          </button>
        )}

        <button
          className="mt-2 flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-button bg-surface text-sm font-medium text-text-primary ring-1 ring-hairline-alt transition hover:bg-surface-hover active:scale-[0.98] disabled:cursor-default disabled:opacity-40 disabled:hover:bg-surface"
          disabled={empty}
          onClick={onOpenCanvas}
          type="button"
        >
          <CanvasIcon className="size-[18px] text-text-secondary" />
          Open canvas
        </button>

        <p className="mt-2.5 text-center text-tag leading-snug text-text-muted">
          {footerNote}
        </p>
      </div>
    </aside>
  );
}
